import * as THREE from 'three';

import {
  LOCOMOTION_CONFIG,
  START_POSITION,
  START_ROTATION,
} from '../config.js';

function applyPosition(target, value) {
  if (Array.isArray(value)) {
    target.set(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0);
  } else {
    target.set(value?.x ?? 0, value?.y ?? 0, value?.z ?? 0);
  }
}

function applyRotation(target, value) {
  if (typeof value === 'number') {
    target.rotation.set(0, value, 0);
  } else if (Array.isArray(value)) {
    target.rotation.set(value[0] ?? 0, value[1] ?? 0, value[2] ?? 0);
  } else {
    target.rotation.set(value?.x ?? 0, value?.y ?? 0, value?.z ?? 0);
  }
}

/** Snap turn and optional head-relative smooth locomotion for a player rig. */
export class Locomotion extends THREE.EventDispatcher {
  constructor({
    renderer = null,
    camera,
    playerRig,
    controllers,
    config = LOCOMOTION_CONFIG,
    movementValidator = null,
    onReset = null,
  } = {}) {
    super();

    if (!camera?.isCamera) throw new TypeError('Locomotion requiere una cámara.');
    if (!playerRig?.isObject3D) throw new TypeError('Locomotion requiere playerRig.');
    if (!controllers?.states) throw new TypeError('Locomotion requiere una instancia de Controllers.');

    this.renderer = renderer;
    this.camera = camera;
    this.playerRig = playerRig;
    this.controllers = controllers;
    this.movementValidator = movementValidator;
    this.onReset = onReset;
    this.enabled = true;

    this.snapTurnAngle = THREE.MathUtils.degToRad(config?.snapTurnAngle ?? 30);
    this.snapDeadzone = config?.snapDeadzone ?? 0.7;
    this.snapResetThreshold = config?.snapReleaseThreshold
      ?? config?.snapResetThreshold
      ?? this.snapDeadzone * 0.55;
    this.snapCooldown = config?.snapCooldown ?? 0.28;
    this.smoothEnabled = config?.smoothEnabled ?? false;
    this.smoothSpeed = config?.smoothSpeed ?? 1.5;
    this.moveDeadzone = config?.moveDeadzone ?? 0.18;

    this._snapReady = true;
    this._snapCooldownRemaining = 0;
    this._forward = new THREE.Vector3(0, 0, -1);
    this._right = new THREE.Vector3(1, 0, 0);
    this._movement = new THREE.Vector3();
    this._localMovement = new THREE.Vector3();
    this._rigWorldPosition = new THREE.Vector3();
    this._candidatePosition = new THREE.Vector3();
    this._pivotBefore = new THREE.Vector3();
    this._pivotLocal = new THREE.Vector3();
    this._parentQuaternion = new THREE.Quaternion();
    this._turnedThisFrame = false;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    this._snapReady = true;
    this._snapCooldownRemaining = 0;
    return this;
  }

  setSmoothEnabled(enabled) {
    this.smoothEnabled = enabled;
    return this;
  }

  setMovementValidator(validator) {
    this.movementValidator = validator;
    return this;
  }

  update(deltaSeconds) {
    if (!this.enabled) return;

    const delta = Math.min(Math.max(deltaSeconds || 0, 0), 0.1);
    this._snapCooldownRemaining = Math.max(0, this._snapCooldownRemaining - delta);
    this._turnedThisFrame = false;
    this.controllers.update();

    this._updateSnapTurn();
    if (this.smoothEnabled && delta > 0 && !this._turnedThisFrame) {
      this._updateSmoothMovement(delta);
    }
  }

  _updateSnapTurn() {
    const rightState = this.controllers.getByHand('right') ?? this.controllers.states[1];
    const gamepad = rightState?.gamepad ?? rightState?.inputSource?.gamepad;
    if (!rightState?.connected || !gamepad?.axes?.length) return;

    const axisX = this._readStickAxis(gamepad, 0);
    const absoluteX = Math.abs(axisX);

    if (absoluteX <= this.snapResetThreshold) this._snapReady = true;
    if (!this._snapReady || this._snapCooldownRemaining > 0 || absoluteX < this.snapDeadzone) return;

    // Positive thumbstick X means a clockwise (right) turn.
    this._applyYaw(axisX > 0 ? -this.snapTurnAngle : this.snapTurnAngle);
    this._snapReady = false;
    this._snapCooldownRemaining = this.snapCooldown;
  }

  _updateSmoothMovement(delta) {
    const leftState = this.controllers.getByHand('left') ?? this.controllers.states[0];
    const gamepad = leftState?.gamepad ?? leftState?.inputSource?.gamepad;
    if (!leftState?.connected || !gamepad?.axes?.length) return;

    const axisX = this._readStickAxis(gamepad, 0);
    const axisY = this._readStickAxis(gamepad, 1);
    const magnitude = Math.sqrt((axisX * axisX) + (axisY * axisY));
    if (magnitude <= this.moveDeadzone) return;

    const scaledMagnitude = Math.min(1, (magnitude - this.moveDeadzone) / (1 - this.moveDeadzone));
    const normalizedX = axisX / magnitude;
    const normalizedY = axisY / magnitude;
    const headCamera = this._getHeadCamera();

    headCamera.getWorldDirection(this._forward);
    this._forward.y = 0;
    if (this._forward.lengthSq() > 0.000001) {
      this._forward.normalize();
    } else {
      this._forward.set(0, 0, -1);
    }
    this._right.set(-this._forward.z, 0, this._forward.x);

    const distance = this.smoothSpeed * scaledMagnitude * delta;
    this._movement.copy(this._right).multiplyScalar(normalizedX * distance);
    this._movement.addScaledVector(this._forward, -normalizedY * distance);

    this.playerRig.getWorldPosition(this._rigWorldPosition);
    this._candidatePosition.copy(this._rigWorldPosition).add(this._movement);
    if (this.movementValidator
      && this.movementValidator(this._candidatePosition, this._movement, this) === false) return;

    this._localMovement.copy(this._movement);
    if (this.playerRig.parent) {
      this.playerRig.parent.getWorldQuaternion(this._parentQuaternion).invert();
      this._localMovement.applyQuaternion(this._parentQuaternion);
    }
    this.playerRig.position.add(this._localMovement);
  }

  _readStickAxis(gamepad, component) {
    const axes = gamepad.axes;
    const offset = Math.max(0, axes.length - 2);
    return axes[offset + component] ?? 0;
  }

  _getHeadCamera() {
    // WebXRManager writes the headset pose back to this user camera. Keeping
    // the query on the rig-parented camera preserves the rig world transform.
    return this.camera;
  }

  _applyYaw(yawRadians) {
    const headCamera = this._getHeadCamera();
    headCamera.getWorldPosition(this._pivotBefore);

    this._pivotLocal.copy(this._pivotBefore);
    if (this.playerRig.parent) this.playerRig.parent.worldToLocal(this._pivotLocal);

    const offsetX = this.playerRig.position.x - this._pivotLocal.x;
    const offsetZ = this.playerRig.position.z - this._pivotLocal.z;
    const cosine = Math.cos(yawRadians);
    const sine = Math.sin(yawRadians);
    this.playerRig.position.x = this._pivotLocal.x + (offsetX * cosine) + (offsetZ * sine);
    this.playerRig.position.z = this._pivotLocal.z - (offsetX * sine) + (offsetZ * cosine);
    this.playerRig.rotateY(yawRadians);
    this.playerRig.updateMatrixWorld(true);
    this._turnedThisFrame = true;

    this.dispatchEvent({ type: 'snapturn', radians: yawRadians });
  }

  snapTurn(direction) {
    if (!direction) return false;
    this._applyYaw(Math.sign(direction) * -this.snapTurnAngle);
    this._snapReady = false;
    this._snapCooldownRemaining = this.snapCooldown;
    return true;
  }

  resetPlayerPosition() {
    if (this.onReset) {
      this.onReset(this);
    } else {
      applyPosition(this.playerRig.position, START_POSITION);
      applyRotation(this.playerRig, START_ROTATION);
      this.playerRig.updateMatrixWorld(true);
    }

    this._snapReady = true;
    this._snapCooldownRemaining = 0;
    this.dispatchEvent({ type: 'reset', playerRig: this.playerRig });
    return this.playerRig;
  }

  dispose() {
    this.enabled = false;
    this.movementValidator = null;
    this.onReset = null;
  }
}

export default Locomotion;
